// "When did this reader last open that screen, on this device?" — the persistence half of the summary
// tiles' "new" badge. The counting half is `lib/summary-freshness.ts`; this module only remembers the
// mark. The React side is `lib/use-last-visited.ts`.
//
// Kept import-free on purpose so the unit test exercises it without dragging the app's dependency
// graph (see orion-web/CLAUDE.md → "Tests (jest)"), and storage is injectable for the same reason —
// these tests run in `node`, where there is no window at all.
//
// Two things this module refuses to do, both of which are the difference between a badge and a bug:
//
//   1. **It never throws.** There is not always a localStorage: SSR has no `window`, a Safari
//      private-mode window throws on the mere ACCESS rather than on the read, a full quota throws on
//      the write, and an embedded webview can have storage off entirely. A summary badge is decoration
//      on a screen full of real content, so a storage failure degrades to "no badge" — it must never
//      take the screen down.
//   2. **It never trusts what it reads back.** The store is user-writable, so a hand-edited or stale
//      value must not reach the arithmetic. Anything non-numeric reads as "no visit recorded", which
//      `newSince` already has a defined answer for (the last 24 hours).

/** The three summary surfaces a reader can open, and which the hub tiles badge against. */
export type VisitedSurface = 'routines' | 'tasks' | 'files';

/** Every surface, in the order the tiles sit in. */
export const VISITED_SURFACES: readonly VisitedSurface[] = ['routines', 'tasks', 'files'];

/**
 * The storage key for a surface.
 *
 * These are NEW keys, so they do NOT inherit the legacy `orion-web:` prefix that the older persisted
 * keys are stuck with — they are named for the PRODUCT (see this package's CLAUDE.md), the same way
 * `symphony-files-view` and `symphony-app-banner-dismissed` are.
 */
export function lastVisitedKey(surface: VisitedSurface): string {
  return `symphony-last-visited:${surface}`;
}

/** Parse a stored value into an epoch-ms stamp, or `null` when there isn't a usable one. Pure. */
export function parseLastVisited(raw: string | null | undefined): number | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
}

/** When this browser last opened the surface, or `null` — including when storage is unreadable. */
export function readLastVisited(surface: VisitedSurface, storage: Pick<Storage, 'getItem'> | null = safeStorage()): number | null {
  try {
    return parseLastVisited(storage?.getItem(lastVisitedKey(surface)));
  } catch {
    return null;
  }
}

/** Record that the reader is looking at the surface now. A failed write is not worth surfacing. */
export function markVisited(
  surface: VisitedSurface,
  nowMs: number = Date.now(),
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): void {
  try {
    storage?.setItem(lastVisitedKey(surface), String(nowMs));
  } catch {
    // Private mode, a full quota, storage blocked by policy — the badge just stays generous.
  }
}

/** `localStorage`, or null where there isn't one — the access itself can throw, so it is guarded too. */
function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
